import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaTimes } from "react-icons/fa";
import { openInNewTab } from "../Utils/utils";
import preload from "../../assets/img/preload.gif";

const ModalMarca = ({ show, onHide, tipoConsulta }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let timer1, timer2;
    
    if (show) {
      setIsLoading(true);
      setIsReady(false);
      timer1 = setTimeout(() => {
        setIsLoading(false);
        // Pequeño delay adicional antes de permitir interacciones
        timer2 = setTimeout(() => {
          setIsReady(true);
        }, 200);
      }, 1000);
    } else {
      setIsLoading(false);
      setIsReady(false);
    }
    
    return () => {
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [show]);

  // Prevenir cierre durante el loading
  const handleClose = () => {
    if (!isLoading && isReady) {
      onHide();
    }
  };
  // Números de WhatsApp para cada marca
  const whatsappPorMarca = {
    ford: "5493513527817",
    fiat: "5493513525903",
    jeep: "5493513743833",
    peugeot: "5493513793585",
    hyundai: "5493517385093",
    gac: "5493517915736",
  };

  // Textos según tipo de consulta (Plan de Ahorro vs Venta directa)
  const getTextoPorMarca = () => {
    if (tipoConsulta === "plan") {
      return "Hola, estoy viendo opciones de plan de ahorro y quiero entender bien cómo funciona, ¿me orientás?";
    }
    return "Hola, estoy viendo opciones de compra directa y quiero entender qué me conviene, ¿me orientás?";
  };

  // Función para detectar si viene de Instagram
  const vieneDeInstagram = () => {
    const currentUrl = window.location.href.toLowerCase();
    const referrer = document.referrer.toLowerCase();

    // Verificar si la URL actual contiene instagram
    const urlTieneInstagram = currentUrl.includes("instagram.com") || currentUrl.includes("l.instagram.com");

    // Verificar si el referrer es de Instagram
    const referrerEsInstagram = referrer.includes("instagram.com");

    return urlTieneInstagram || referrerEsInstagram;
  };

  /**
   * Parámetros de Meta/Instagram que confunden al CRM:
   * - fbclid: Facebook Click ID - los CRMs lo interpretan como "Meta Ads" aunque sea tráfico orgánico
   * - _fbp: Facebook browser ID - puede influir en la atribución
   * Cuando viene de Instagram orgánico, los eliminamos para que utm_source=INSTAGRAM ORGANICO
   * sea la fuente de verdad en el CRM.
   */
  const META_AD_PARAMS = ["fbclid", "_fbp"];

  const getCurrentPageUrlWithUtm = (marcaKey) => {
    const applyUtmDefaults = (url) => {
      const hasUtmSource = url.searchParams.has("utm_source");
      const currentUtmSource = hasUtmSource ? url.searchParams.get("utm_source") : null;

      if (vieneDeInstagram()) {
        if (!hasUtmSource || currentUtmSource?.toLowerCase() === "ig") {
          url.searchParams.set("utm_source", "INSTAGRAM ORGANICO");
        }
        META_AD_PARAMS.forEach((param) => url.searchParams.delete(param));
      } else if (!hasUtmSource) {
        url.searchParams.set("utm_source", "directo");
      }

      if (!url.searchParams.has("utm_medium")) {
        url.searchParams.set("utm_medium", "web");
      }

      if (!url.searchParams.has("utm_campaign")) {
        url.searchParams.set("utm_campaign", marcaKey);
      }

      return url.toString();
    };

    try {
      const currentUrl = new URL(window.location.href);
      return applyUtmDefaults(currentUrl);
    } catch (e) {
      const baseUrl = window.location.href.split("?")[0].split("#")[0];
      const fallbackUrl = new URL(baseUrl);
      return applyUtmDefaults(fallbackUrl);
    }
  };

  const marcasTodas = [
    { key: "ford", nombre: "FORD" },
    { key: "fiat", nombre: "FIAT" },
    { key: "jeep", nombre: "JEEP" },
    { key: "peugeot", nombre: "PEUGEOT" },
    { key: "hyundai", nombre: "HYUNDAI" },
    { key: "gac", nombre: "GAC" },
  ];

  const marcasSinPlan = ["gac", "hyundai"];
  const marcas =
    tipoConsulta === "plan"
      ? marcasTodas.filter((marca) => !marcasSinPlan.includes(marca.key))
      : marcasTodas;

  const handleMarcaClick = (marcaKey) => {
    // Prevenir clicks durante loading o antes de estar listo
    if (isLoading || !isReady) {
      return;
    }

    const numero = whatsappPorMarca[marcaKey];
    const texto = getTextoPorMarca();

    const pageUrlWithUtm = getCurrentPageUrlWithUtm(marcaKey);

    const textToSend = texto + "\n" + pageUrlWithUtm;

    // Construir la URL de WhatsApp con el texto completo que incluye la URL con UTMs
    const url = `https://api.whatsapp.com/send?phone=${numero}&text=${encodeURIComponent(textToSend)}`;

    openInNewTab(url);
    onHide();
  };

  return (
    <Modal 
      show={show} 
      onHide={handleClose} 
      centered 
      dialogClassName="modal-marca" 
      backdropClassName="modal-backdrop"
      backdrop={isReady ? true : "static"} // Prevenir cierre con backdrop durante loading
      keyboard={isReady} // Prevenir cierre con ESC durante loading
    >
      <Modal.Body 
        className="modal-marca-body" 
        style={isLoading ? { backgroundColor: "#fff" } : {}}
      >
        <button 
          className="modal-close-btn" 
          onClick={handleClose} 
          aria-label="Cerrar"
          disabled={isLoading || !isReady}
          style={{ 
            opacity: (isLoading || !isReady) ? 0.5 : 1,
            cursor: (isLoading || !isReady) ? "not-allowed" : "pointer",
            pointerEvents: (isLoading || !isReady) ? "none" : "auto"
          }}
        >
          <FaTimes />
        </button>

        {isLoading ? (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "300px", backgroundColor: "#fff" }}>
            <img src={preload} alt="Cargando..." style={{ maxWidth: "150px" }} />
            <p className="mt-3" style={{ color: "#666" }}>Cargando...</p>
          </div>
        ) : (
          <>
            <h2 className="modal-title">¿Con qué marca necesitás hablar?</h2>
            <p className="modal-subtitle">Seleccioná una opción y continuá por WhatsApp</p>

            <div className="modal-marcas-list">
              {marcas.map((marca) => (
                <Button 
                  key={marca.key} 
                  className="modal-marca-btn" 
                  onClick={() => handleMarcaClick(marca.key)}
                  disabled={!isReady}
                  style={{
                    pointerEvents: !isReady ? "none" : "auto"
                  }}
                >
                  {marca.nombre}
                </Button>
              ))}
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ModalMarca;

